import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Dashboard/Header/Header';
import {
  Search,
  Plus,
  SlidersHorizontal as Filters,
  Pencil,
  MoreVertical,
  Eye,
  Trash2,
  ToggleLeft as StatusToggle,
  X,
  Loader2,
  Calendar
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styles from './Employees.module.css';
import Pagination from '../../components/Common/Pagination/Pagination';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const Employees = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    department_id: '',
    designation_id: '',
    status: ''
  });
  const [filterData, setFilterData] = useState({
    departments: [],
    designations: []
  });

  // Permission guards
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create employees');
  const canEdit   = isAdmin || userPermissions.includes('edit employees');
  const canDelete = isAdmin || userPermissions.includes('delete employees');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (searchTerm !== debouncedSearch) {
        setPagination(prev => ({ ...prev, current_page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (id, e) => {
    if (openMenuIndex === id) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const menuHeight = 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top;
      let transform = 'none';

      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        top = rect.top - 4;
        transform = 'translateY(-100%)';
      } else {
        top = rect.bottom + 4;
        transform = 'none';
      }

      setMenuPosition({
        top,
        left: Math.max(10, rect.right - 160),
        transform
      });
      setOpenMenuIndex(id);
    }
  };
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleAddClick = () => {
    navigate('/employees/add');
  };

  const handleEditClick = (employee) => {
    navigate(`/employees/edit/${employee.id}`);
  };

  const handleDelete = async (employee) => {
    setOpenMenuIndex(null);
    
    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Item</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Employee Record?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete <strong>${employee.employee_code}: ${employee.employee_name}</strong>?<br/>
          This action cannot be undone and may affect linked records.
        </p>

      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/employees/${employee.id}`);
          if (response.data.success) {
            toast.success('Employee deleted successfully');
            fetchEmployees();
          }
        } catch (error) {
          console.error('Error deleting employee:', error);
          toast.error(error.response?.data?.message || 'Failed to delete employee');
        }
      }
    });
  };

  const handleUpdateStatus = async (employee) => {
    setOpenMenuIndex(null);
    const newStatus = employee.status === 1 ? 0 : 1;
    const statusText = newStatus === 1 ? 'Active' : 'Inactive';

    try {
      const response = await api.put(`/employees/${employee.id}`, {
        status: newStatus
      });
      if (response.data.success) {
        toast.success(`Employee status updated to ${statusText}`);
        fetchEmployees();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleRefresh = () => {
    fetchEmployees();
  };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const [deptRes, desigRes] = await Promise.all([
        api.get('/departments'),
        api.get('/designations')
      ]);
      setFilterData({
        departments: deptRes.data.data.data || deptRes.data.data || [],
        designations: desigRes.data.data.data || desigRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page, debouncedSearch, filters]);

  const fetchEmployees = async () => {
    try {
      let url = `/employees?page=${pagination.current_page}&per_page=${pagination.per_page}&search=${encodeURIComponent(debouncedSearch)}`;
      
      if (filters.department_id) url += `&department_id=${filters.department_id}`;
      if (filters.designation_id) url += `&designation_id=${filters.designation_id}`;
      if (filters.status !== '') url += `&status=${filters.status}`;

      const response = await api.get(url);
      if (response.data.success) {
        setEmployees(response.data.data.data);
        const { current_page, last_page, total, per_page } = response.data.data;
        setPagination({ current_page, last_page, total, per_page });
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      department_id: '',
      designation_id: '',
      status: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = ['#fee2e2', '#fef3c7', '#dcfce7', '#d1fae5', '#e0f2fe', '#f3e8ff', '#fce7f3'];
    const charCodeSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return colors[charCodeSum % colors.length];
  };

  const getAvatarTextColor = (bgColor) => {
    // Basic contrast check - returns a darker version of the color for text
    return bgColor.replace('f', '9').replace('e', '7').replace('d', '5');
  };

  return (
    <div className={`${styles['employees-layout']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Employees</h1>
              <p>View and manage all organization personnel records from a central directory.</p>
            </div>
            {canCreate && (
              <button className={styles['add-btn']} onClick={handleAddClick}>
                <Plus size={20} />
                <span>Add Employee</span>
              </button>
            )}
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search by name, code, or department..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => setPagination(prev => ({ ...prev, per_page: option.value, current_page: 1 }))}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button 
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.department_id || filters.designation_id || filters.status !== '') && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Employees</h3>
                  <p>Filter personnel by department, designation, or status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Department</label>
                  <CustomSelect
                    value={filters.department_id ? { 
                      value: filters.department_id, 
                      label: filterData.departments.find(d => d.id === filters.department_id)?.departement_name 
                    } : null}
                    onChange={(opt) => handleFilterChange('department_id', opt ? opt.value : '')}
                    options={filterData.departments.map(d => ({ value: d.id, label: d.departement_name }))}
                    placeholder="All Departments"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Designation</label>
                  <CustomSelect
                    value={filters.designation_id ? { 
                      value: filters.designation_id, 
                      label: filterData.designations.find(d => d.id === filters.designation_id)?.designation_name 
                    } : null}
                    onChange={(opt) => handleFilterChange('designation_id', opt ? opt.value : '')}
                    options={filterData.designations.map(d => ({ value: d.id, label: d.designation_name }))}
                    placeholder="All Designations"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status !== '' ? { 
                      value: filters.status, 
                      label: filters.status === '1' ? 'Active' : 'Inactive' 
                    } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={[
                      { value: '1', label: 'Active' },
                      { value: '0', label: 'Inactive' }
                    ]}
                    placeholder="All Status"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            {/* Desktop View */}
            <div className={styles['table-wrapper']}>
              <table className={styles['employee-table']}>
                <thead>
                  <tr>
                    <th>EMPLOYEE & ID</th>
                    <th>DESIGNATION</th>
                    <th>DEPARTMENT</th>
                    <th>ROLE</th>
                    <th>STATUS</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length > 0 ? (
                    employees.map((employee) => (
                      <tr key={employee.id}>
                        <td className={styles['name-cell']}>
                          <div className={styles['user-info']}>
                            <div
                              className={styles['avatar']}
                              style={{
                                backgroundColor: getAvatarColor(employee.employee_name),
                                color: getAvatarTextColor(getAvatarColor(employee.employee_name))
                              }}
                            >
                              {getInitials(employee.employee_name)}
                            </div>
                            <div className={styles['name-code-wrapper']}>
                              <span className={styles['name-text']}>{employee.employee_name}</span>
                              <span className={styles['code-text']}>{employee.employee_code}</span>
                            </div>
                          </div>
                        </td>
                        <td>{employee.designation?.designation_name || 'N/A'}</td>
                        <td>{employee.department?.departement_name || 'N/A'}</td>
                        <td style={{ textTransform: 'capitalize' }}>{employee.role || 'N/A'}</td>
                        <td>
                          <span className={`${styles['status-pill']} ${employee.status === 1 ? styles['active'] : styles['inactive']}`}>
                            {employee.status === 1 ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className={styles['actions-cell']}>
                          <div className={`action-wrapper ${styles['action-wrapper']}`}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === employee.id ? styles['active'] : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleActionMenu(employee.id, e);
                              }}
                            >
                              <MoreVertical size={18} />
                            </button>

                            {openMenuIndex === employee.id && (
                              <div
                                className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                                style={{
                                  top: menuPosition.top,
                                  left: menuPosition.left,
                                  transform: menuPosition.transform || 'none'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button className={styles['dropdown-item']} onClick={() => navigate(`/employees/view/${employee.id}`)}>
                                  <Eye size={16} /> View
                                </button>
                                <button 
                                  className={styles['dropdown-item']} 
                                  onClick={() => {
                                    setOpenMenuIndex(null);
                                    navigate(`/employees/attendance/view/${employee.id}`);
                                  }}
                                >
                                  <Calendar size={16} /> View Attendance
                                </button>
                                {canEdit && (
                                  <button className={styles['dropdown-item']} onClick={() => {
                                    handleEditClick(employee);
                                    setOpenMenuIndex(null);
                                  }}>
                                    <Pencil size={16} /> Edit
                                  </button>
                                )}
                                {canEdit && (
                                  <button className={styles['dropdown-item']} onClick={() => handleUpdateStatus(employee)}>
                                    <StatusToggle size={16} /> Set as {employee.status === 1 ? 'Inactive' : 'Active'}
                                  </button>
                                )}
                                {canDelete && (
                                  <>
                                    <div className={styles['dropdown-divider']}></div>
                                    <button 
                                      className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                                      onClick={() => handleDelete(employee)}
                                    >
                                      <Trash2 size={16} /> Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : !loading && (
                    <NoData message="No employees found." colSpan={6} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['employee-cards']}>
              {employees.length > 0 ? (
                employees.map((employee) => (
                  <div key={employee.id} className={styles['employee-card']}>
                    <div className={styles['card-main']}>
                      <div
                        className={styles['card-avatar']}
                        style={{
                          backgroundColor: getAvatarColor(employee.employee_name),
                          color: getAvatarTextColor(getAvatarColor(employee.employee_name))
                        }}
                      >
                        {getInitials(employee.employee_name)}
                      </div>
                      <div className={styles['card-info']}>
                        <span className={styles['card-name']}>{employee.employee_name}</span>
                        <span className={styles['card-code']}>{employee.employee_code}</span>
                      </div>
                      <div className={`action-wrapper ${styles['action-wrapper']}`}>
                        <button
                          className={`${styles['action-btn']} ${openMenuIndex === employee.id ? styles['active'] : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleActionMenu(employee.id, e);
                          }}
                        >
                          <MoreVertical size={18} />
                        </button>

                        {openMenuIndex === employee.id && (
                          <div
                            className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                            style={{
                              top: menuPosition.top,
                              left: menuPosition.left,
                              transform: menuPosition.transform || 'none'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button className={styles['dropdown-item']} onClick={() => navigate(`/employees/view/${employee.id}`)}>
                              <Eye size={16} /> View
                            </button>
                            <button 
                              className={styles['dropdown-item']} 
                              onClick={() => {
                                setOpenMenuIndex(null);
                                navigate(`/employees/attendance/view/${employee.id}`);
                              }}
                            >
                              <Calendar size={16} /> View Attendance
                            </button>
                            {canEdit && (
                              <button className={styles['dropdown-item']} onClick={() => {
                                handleEditClick(employee);
                                setOpenMenuIndex(null);
                              }}>
                                <Pencil size={16} /> Edit
                              </button>
                            )}
                            {canEdit && (
                              <button className={styles['dropdown-item']} onClick={() => handleUpdateStatus(employee)}>
                                <StatusToggle size={16} /> Set as {employee.status === 1 ? 'Inactive' : 'Active'}
                              </button>
                            )}
                            {canDelete && (
                              <>
                                <div className={styles['dropdown-divider']}></div>
                                <button 
                                  className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                                  onClick={() => handleDelete(employee)}
                                >
                                  <Trash2 size={16} /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Designation</span>
                        <span className={styles['detail-value']}>{employee.designation?.designation_name || 'N/A'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Department</span>
                        <span className={styles['detail-value']}>{employee.department?.departement_name || 'N/A'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Role</span>
                        <span className={styles['detail-value']} style={{ textTransform: 'capitalize' }}>{employee.role || 'N/A'}</span>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <span className={`${styles['status-pill']} ${employee.status === 1 ? styles['active'] : styles['inactive']}`}>
                        {employee.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))
              ) : !loading && (
                <NoData message="No employees found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
            <div className={styles['mobile-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
          </div>
        
          <Footer />
        </main>
      </div>

    </div>
  );
};

export default Employees;
